import { z } from 'zod';
import { HttpUrl } from './common/url.js';
import { Taxonomy } from './taxonomy.js';
import { CursorQuery } from './common/pagination.js';
/** Communities & groups (FR-COMM-01…11, AC-COMM-01…06). */
export const CommunityVisibility = z.enum(['public', 'private']);
export const CommunityJoinMode = z.enum(['public', 'approval', 'invite']);
export const CommunityMembershipPlan = z.enum(['free', 'paid']);
export const CommunityReach = z.enum(['5 mi', '15 mi', '50 mi', 'Everywhere']);
/** Group-wide permissions are separate from personal directory privacy. */
export const CommunitySettings = z.object({
    whoCanPost: z.enum(['members', 'admins']),
    approvePosts: z.boolean(),
    /** Whether non-admin members can send. Active members retain read access when false. */
    chatEnabled: z.boolean(),
    whoCanCreateMeetings: z.enum(['members', 'admins']),
    whoCanAnnounce: z.enum(['members', 'admins']),
    autoWelcome: z.boolean(),
    directoryEnabled: z.boolean(),
    attendanceDetailsVisible: z.boolean(),
});
export const DEFAULT_COMMUNITY_SETTINGS = {
    whoCanPost: 'members',
    approvePosts: false,
    chatEnabled: true,
    whoCanCreateMeetings: 'admins',
    whoCanAnnounce: 'admins',
    autoWelcome: true,
    directoryEnabled: true,
    attendanceDetailsVisible: true,
};
/** Joined users are full group members. */
export const MembershipRole = z.enum(['admin', 'moderator', 'member']);
export const MembershipState = z.enum(['none', 'requested', 'joined', 'admin']);
export const GroupCoordinates = z.tuple([
    z.number().min(-180).max(180),
    z.number().min(-90).max(90),
]);
export const CreateCommunityInput = Taxonomy.extend({
    name: z.string().trim().min(2).max(80),
    description: z.string().trim().max(2_000).optional(),
    thumbnailUrl: HttpUrl.optional(),
    /**
     * Banner imagery for the community header, in the order it is shown.
     * Bounded at 10 like a post's attachments, and defaulted so a client that
     * predates the field still creates communities with no body change.
     *
     * URLs, not uploads: the storage module mints these (POST /storage/images)
     * and the key encodes its own owner, so a community never becomes a second
     * place file ownership is decided.
     */
    coverImages: z.array(HttpUrl).max(10).default([]),
    joinMode: CommunityJoinMode.default('public'),
    membership: CommunityMembershipPlan.default('free'),
    monthlyFeeMinor: z.number().int().positive().optional(),
    locationName: z.string().trim().max(200).optional(),
    locationCoordinates: GroupCoordinates.optional(),
    reach: CommunityReach.default('15 mi'),
    homeCourt: z.string().trim().max(200).optional(),
    /** Initial invite list from connections (FR-COMM-06). */
    inviteUserIds: z.array(z.string()).max(100).default([]),
});
/**
 * Edit an existing community (community admins only). A PATCH like a post's
 * edit: every field optional, only what is sent is touched, and an empty
 * patch is rejected rather than silently doing nothing.
 *
 * `description` and `thumbnailUrl` are NULLABLE as well as optional, which is
 * the difference between "leave it alone" (omit) and "clear it" (send null) —
 * without that an admin could set a thumbnail but never remove one. Sending
 * `coverImages: []` clears them the same way, no null needed, because the
 * field is already a list.
 *
 * Taxonomy is editable: a miscategorised community has no other route back,
 * and both values stay required so AC-GL-17 cannot be dodged by patching one
 * away. Join mode is the new design's single source of truth for discovery and
 * entry; the API derives the legacy stored `visibility` value from it.
 */
export const UpdateCommunityInput = z
    .object({
    settings: CommunitySettings.partial()
        .refine((patch) => Object.keys(patch).length > 0, 'No settings supplied')
        .optional(),
    name: z.string().trim().min(2).max(80).optional(),
    description: z.string().trim().max(2_000).nullable().optional(),
    thumbnailUrl: HttpUrl.nullable().optional(),
    coverImages: z.array(HttpUrl).max(10).optional(),
    joinMode: CommunityJoinMode.optional(),
    membership: CommunityMembershipPlan.optional(),
    monthlyFeeMinor: z.number().int().positive().nullable().optional(),
    locationName: z.string().trim().max(200).nullable().optional(),
    locationCoordinates: GroupCoordinates.nullable().optional(),
    reach: CommunityReach.optional(),
    homeCourt: z.string().trim().max(200).nullable().optional(),
    category1: Taxonomy.shape.category1.optional(),
    category2: Taxonomy.shape.category2.optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Empty patch' });
export const CommunityView = Taxonomy.extend({
    settings: CommunitySettings.extend({
        attendanceDetailsVisible: z.boolean().default(true),
    }).default(DEFAULT_COMMUNITY_SETTINGS),
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    thumbnailUrl: z.string().optional(),
    /**
     * Defaulted rather than optional so callers always get an array to map over,
     * while a response from an API that predates the field still parses — the
     * api-client validates responses, so a required field here would be a
     * deploy-order constraint dressed up as an additive change.
     */
    coverImages: z.array(z.string()).default([]),
    visibility: CommunityVisibility,
    joinMode: CommunityJoinMode,
    membership: CommunityMembershipPlan,
    monthlyFeeMinor: z.number().int().positive().optional(),
    locationName: z.string().optional(),
    locationCoordinates: GroupCoordinates.optional(),
    reach: CommunityReach,
    homeCourt: z.string().optional(),
    memberCount: z.number().int().nonnegative(),
    archivedAt: z.coerce.date().optional(),
    /** For the viewer, when known. */
    myState: MembershipState.optional(),
    /** A pending invitation addressed to the current viewer, never another member. */
    isInvited: z.boolean().optional(),
    /** Admin-only management counters. */
    pendingRequestCount: z.number().int().nonnegative().optional(),
    pendingInviteCount: z.number().int().nonnegative().optional(),
    /** "8 Mutual Connects" — viewer's connections who are members (FR-COMM-08). */
    mutualCount: z.number().int().nonnegative().optional(),
    createdAt: z.coerce.date(),
});
export const CommunitySetupProgress = z.object({
    detailsComplete: z.boolean(),
    membersInvited: z.boolean(),
    welcomePosted: z.boolean(),
    meetingScheduled: z.boolean(),
});
export const CommunityListQuery = CursorQuery.extend({
    q: z.string().trim().min(2).max(100).optional(),
    category1: Taxonomy.shape.category1.optional(),
    category2: Taxonomy.shape.category2.optional(),
});
export const CommunityBrowseQuery = CommunityListQuery.extend({
    withinReach: z.enum(['true', 'false']).optional(),
    q: z.string().trim().min(2).max(100).optional(),
    latitude: z.coerce.number().min(-90).max(90).optional(),
    longitude: z.coerce.number().min(-180).max(180).optional(),
}).refine((query) => (query.latitude === undefined) === (query.longitude === undefined), {
    message: 'Supply both latitude and longitude',
});
export const InviteInput = z.object({ userIds: z.array(z.string()).min(1).max(100) });
export const MembershipStateView = z.object({ communityId: z.string(), state: MembershipState });
export const CommunityMemberPosition = z.enum([
    'Member',
    'Chairman',
    'Vice Chairman',
    'Secretary',
    'Treasurer',
    'Advertising',
    'Member Support',
]);
export const GroupPositionRef = z.object({ id: z.string(), name: z.string() });
export const GroupPositionView = GroupPositionRef.extend({
    description: z.string().optional(),
    assignedCount: z.number().int().nonnegative(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const PositionName = z
    .string()
    .transform((name) => name.normalize('NFKC').trim().replace(/\s+/g, ' '))
    .pipe(z.string().min(2).max(60))
    .refine((name) => name.toLowerCase() !== 'member', 'Member is the default position');
export const CreateGroupPositionInput = z.object({
    name: PositionName,
    description: z.string().trim().max(200).optional(),
});
export const UpdateGroupPositionInput = z
    .object({
    name: PositionName.optional(),
    description: z.string().trim().max(200).nullable().optional(),
    expectedUpdatedAt: z.coerce.date(),
})
    .refine((input) => input.name !== undefined || input.description !== undefined, 'No position changes supplied');
export const AssignGroupPositionInput = z
    .object({ userIds: z.array(z.string().min(1)).min(1).max(100) })
    .refine((input) => new Set(input.userIds).size === input.userIds.length, 'Select each member once');
export const DeleteGroupPositionQuery = z.object({
    replacementPositionId: z.string().min(1).optional(),
    expectedRevision: z.coerce.number().int().nonnegative(),
});
export const MemberRow = z.object({
    userId: z.string(),
    displayName: z.string().nullable(),
    role: MembershipRole,
    /** Display position is independent of access; older memberships default to Member in the UI. */
    position: CommunityMemberPosition.optional(),
    positionId: z.string().nullable().optional(),
    positionInfo: GroupPositionRef.optional(),
    since: z.coerce.date(),
    email: z.string().email().optional(),
});
export const CommunityMembersQuery = CursorQuery.extend({
    directory: z.enum(['true', 'false']).optional(),
    userId: z.string().min(1).optional(),
    q: z.string().trim().max(100).optional(),
    /** members filters access roles; ordinary filters the default Member position, including admins. */
    filter: z.enum(['all', 'admins', 'members', 'businesses', 'officers', 'ordinary']).optional(),
});
export const JoinRequestRow = z.object({
    userId: z.string(),
    displayName: z.string().nullable(),
    requestedAt: z.coerce.date(),
});
export const CommunityInvitationRow = z.object({
    id: z.string(),
    community: CommunityView,
    inviterId: z.string(),
    inviterName: z.string().nullable(),
    invitedAt: z.coerce.date(),
});
export const SentCommunityInviteRow = z.object({
    userId: z.string(),
    displayName: z.string().nullable(),
    invitedAt: z.coerce.date(),
});
export const UpdateCommunityMemberInput = z
    .object({
    role: z.enum(['admin', 'moderator', 'member']).optional(),
    position: CommunityMemberPosition.optional(),
    positionId: z.string().min(1).nullable().optional(),
})
    .refine((input) => input.role !== undefined || input.position !== undefined || input.positionId !== undefined, {
    message: 'Choose a role or position to update',
})
    .refine((input) => input.position === undefined || input.positionId === undefined, 'Choose a position ID or a legacy position, not both');
// Group calendar & meetings — member-visible, administered by group admins.
export const CommunityMeetingMode = z.enum(['in_person', 'online', 'hybrid']);
export const CommunityMeetingRsvp = z.enum(['going', 'maybe', 'not_going']);
export const CommunityMeetingAgendaItem = z.object({
    title: z.string().trim().min(1).max(160),
    details: z.string().trim().max(500).optional(),
    /** Offset from the meeting start; avoids timezone drift in agenda rows. */
    offsetMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60),
    durationMinutes: z
        .number()
        .int()
        .min(5)
        .max(8 * 60)
        .optional(),
    presenter: z.string().trim().max(100).optional(),
});
const CommunityMeetingFields = z.object({
    photoUrl: HttpUrl.optional(),
    attendanceVisible: z.boolean().optional(),
    meetingType: z.enum(['meeting', 'event', 'game']).default('meeting'),
    title: z.string().trim().min(2).max(120),
    description: z.string().trim().max(2_000).optional(),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    timezone: z.string().trim().min(1).max(100),
    mode: CommunityMeetingMode,
    location: z.string().trim().max(300).optional(),
    videoUrl: HttpUrl.optional(),
    agenda: z.array(CommunityMeetingAgendaItem).max(20).default([]),
    reminderMinutes: z
        .array(z
        .number()
        .int()
        .min(0)
        .max(30 * 24 * 60))
        .max(5)
        .default([1440, 60]),
    postToChat: z.boolean().default(true),
    /** Null/omitted invites the whole group; otherwise invite these active members. */
    invitedMemberIds: z
        .array(z.string().min(1).max(128))
        .min(1)
        .max(500)
        .refine((ids) => new Set(ids).size === ids.length, 'Members must be unique')
        .nullable()
        .optional(),
});
export const CreateCommunityMeetingInput = CommunityMeetingFields.refine((value) => value.endsAt > value.startsAt, {
    path: ['endsAt'],
    message: 'End time must be after start time',
})
    .refine((value) => value.mode === 'online' || !!value.location, {
    path: ['location'],
    message: 'Location is required for in-person meetings',
})
    .refine((value) => value.mode === 'in_person' || !!value.videoUrl, {
    path: ['videoUrl'],
    message: 'Video link is required for online meetings',
});
export const UpdateCommunityMeetingInput = CommunityMeetingFields.partial()
    .extend({
    photoUrl: HttpUrl.nullable().optional(),
    meetingType: CommunityMeetingFields.shape.meetingType.unwrap().optional(),
    agenda: CommunityMeetingFields.shape.agenda.unwrap().optional(),
    reminderMinutes: CommunityMeetingFields.shape.reminderMinutes.unwrap().optional(),
    postToChat: CommunityMeetingFields.shape.postToChat.unwrap().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Empty patch' });
export const CommunityMeetingView = CommunityMeetingFields.extend({
    // Deleting invited accounts can leave a targeted meeting with no remaining invitees.
    invitedMemberIds: z.array(z.string().min(1).max(128)).max(500).nullable().optional(),
    id: z.string(),
    communityId: z.string(),
    createdBy: z.string(),
    myRsvp: CommunityMeetingRsvp.nullable(),
    goingCount: z.number().int().nonnegative(),
    maybeCount: z.number().int().nonnegative(),
    notGoingCount: z.number().int().nonnegative().optional(),
    canViewAttendance: z.boolean().optional(),
    canManage: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CommunityCalendarQuery = z.object({
    from: z.coerce.date(),
    to: z.coerce.date(),
});
export const CommunityMeetingRsvpInput = z.object({ status: CommunityMeetingRsvp });
export const MeetingAttendanceQuery = CursorQuery.extend({
    status: CommunityMeetingRsvp.default('going'),
});
export const MeetingAttendee = z.object({
    userId: z.string(),
    status: CommunityMeetingRsvp,
    respondedAt: z.coerce.date(),
    positionName: z.string(),
    role: MembershipRole,
});
export const PastMeetingPhoto = z.object({
    url: HttpUrl,
    meetingId: z.string(),
    title: z.string(),
});
// Group announcements & celebrations — screen 76.
export const CommunityAnnouncementType = z.enum([
    'new_member',
    'admin_announcement',
    'celebration',
]);
export const CommunityAnnouncementReaction = z.enum(['welcome', 'celebrate']);
const CommunityAnnouncementFields = z.object({
    type: CommunityAnnouncementType,
    title: z.string().trim().min(2).max(160),
    description: z.string().trim().min(1).max(2_000),
    memberId: z.string().min(1).nullable().default(null),
    meetingId: z.string().min(1).nullable().default(null),
    milestoneKind: z.string().trim().min(1).max(80).nullable().default(null),
    tags: z.array(z.string().trim().min(1).max(60)).max(10).default([]),
    isPinned: z.boolean().default(false),
});
function validAnnouncementLinks(value) {
    if (value.type === 'new_member')
        return !!value.memberId && !value.meetingId;
    if (value.type === 'celebration')
        return !!value.memberId && !!value.milestoneKind && !value.meetingId;
    return !value.memberId && !value.milestoneKind;
}
export const CreateCommunityAnnouncementInput = CommunityAnnouncementFields.refine(validAnnouncementLinks, { message: 'Announcement fields do not match its type' });
export const UpdateCommunityAnnouncementInput = z
    .object({
    title: z.string().trim().min(2).max(160).optional(),
    description: z.string().trim().min(1).max(2_000).optional(),
    meetingId: z.string().min(1).nullable().optional(),
    milestoneKind: z.string().trim().min(1).max(80).optional(),
    tags: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Empty patch' });
export const CommunityAnnouncementView = CommunityAnnouncementFields.extend({
    id: z.string(),
    communityId: z.string(),
    authorId: z.string(),
    memberName: z.string().nullable(),
    reactionKind: CommunityAnnouncementReaction.nullable(),
    reactionCount: z.number().int().nonnegative(),
    reactedByMe: z.boolean(),
    publishedAt: z.coerce.date(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CommunityAnnouncementQuery = CursorQuery.extend({
    section: z.enum(['announce', 'celebrate']).optional(),
});
export const CommunityAnnouncementSummary = z.object({
    unreadCount: z.number().int().nonnegative(),
    announcementUnreadCount: z.number().int().nonnegative().default(0),
    celebrationUnreadCount: z.number().int().nonnegative().default(0),
    pinnedAnnouncement: CommunityAnnouncementView.nullable(),
});
export const ReadCommunityAnnouncementsInput = z.object({
    throughAnnouncementId: z.string().min(1),
});
export const DirectoryPrivacyInput = z.object({ showEmail: z.boolean() });
export const DirectoryPrivacyView = DirectoryPrivacyInput.extend({
    email: z.string().email().nullable(),
});
//# sourceMappingURL=community.js.map